import type { Ref, DeepReadonly } from 'vue'
import type { Company } from '@/entities/company'
import type { ScheduleSetting } from '@/entities/meeting'

interface CronofyToken {
  element_token: {
    token: string
    subs: string[]
  }
}

interface State {
  loading: boolean
  error: Error | null
  company: Company | null
  token: string | null
  subs: string[]
  meetingSettings: ScheduleSetting | null
}

export interface UseMeetingRequestSettings {
  state: DeepReadonly<Ref<State>>
}

const SETTINGS_URL = 'v2/meetings/request-settings'

export function useMeetingRequestSettings({ id }: { id: Ref<number | string> }): UseMeetingRequestSettings {
  const state = useState<State>('meetingRequestSettings', () => ({
    loading: false,
    error: null,
    company: null,
    token: null,
    meetingSettings: null,
    subs: [],
  }))

  watch(
    id,
    async (id: number | string): Promise<void> => {
      state.value.loading = true
      state.value.meetingSettings = null

      try {
        const [meetingSettings, cronofyToken] = await _getRequestSettings(id)

        state.value.meetingSettings = meetingSettings
        state.value.company = meetingSettings.company as Company
        state.value.token = cronofyToken?.element_token.token as string
        state.value.subs = cronofyToken?.element_token.subs as string[]
      } catch (e) {
        if (e instanceof Error) {
          state.value.error = e
        }
      } finally {
        state.value.loading = false
      }
    },
    { immediate: true }
  )

  async function _getRequestSettings(id: string | number): Promise<[ScheduleSetting, CronofyToken | null]> {
    const meetingSettings = await $fetch<ScheduleSetting>(`${SETTINGS_URL}/${id}`, {
      ...useFetchSharedOptions(),
      params: { include: ['able_to_book'] },
    })

    if (!meetingSettings.able_to_book) return [meetingSettings, null]

    const cronofyToken = await $fetch<CronofyToken>(`${SETTINGS_URL}/${meetingSettings.id}/availability-token`, {
      ...useFetchSharedOptions(),
    })

    return [meetingSettings, cronofyToken]
  }

  return { state: readonly(state) }
}
